import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { SeoHead } from "@/components/resources/SeoHead";
import { Breadcrumbs } from "@/components/resources/Breadcrumbs";
import { CtaBlock, InlineCtaBanner } from "@/components/resources/CtaBlock";
import { ProofBlock } from "@/components/resources/ProofBlock";
import { FaqSection, RelatedPages } from "@/components/resources/FaqSection";
import { LandingDetails } from "@/components/resources/LandingDetails";
import { CalculatorWidget } from "@/components/resources/CalculatorWidget";
import { ComparisonTable } from "@/components/resources/ComparisonTable";
import { ChecklistBlock, KeyFeatures } from "@/components/resources/ChecklistBlock";
import { ResourceArticleBody } from "@/components/resources/ResourceArticleBody";
import "@/components/resources/resource-body.css";
import { Spinner } from "@/components/shared";
import { publicCms } from "@/lib/cms";
import { track } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

const SECTION_LABELS = {
  "ai-costs": "AI Costs & Pricing", "ai-roi": "AI ROI", tools: "Tools & Calculators",
  departments: "By Department", industries: "By Industry",
  "ai-governance": "AI Governance", guides: "Guides",
};

export default function ResourcePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, page: null, notFound: false });

  useEffect(() => {
    let active = true;
    const path = location.pathname.replace(/\/$/, "") || "/resources";
    setState({ loading: true, page: null, notFound: false });
    publicCms
      .page(path)
      .then(({ data }) => {
        if (!active) return;
        if (data.type === "redirect") {
          navigate(data.to, { replace: true });
          return;
        }
        setState({ loading: false, page: data.page, notFound: false });
        track("resource_page_view", { path, title: data.page?.title });
      })
      .catch(() => active && setState({ loading: false, page: null, notFound: true }));
    return () => {
      active = false;
    };
  }, [location.pathname, navigate]);

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner />
      </div>
    );
  }

  if (state.notFound || !state.page) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <title>Resource not found | NetLift</title>
          <meta name="robots" content="noindex,follow" />
          <h1 className="font-heading text-3xl font-semibold text-navy">Resource not found</h1>
          <p className="mt-3 text-slate-500">This page may have moved or is not published yet.</p>
          <Link to="/resources" className="mt-6 inline-block font-medium text-editorial">
            ← Back to all resources
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const p = state.page;
  return (
    <div className="min-h-screen bg-background">
      <SeoHead meta={p.meta} schemaJson={p.schema_json} />
      <Navbar />
      <article className="mx-auto max-w-3xl px-6 py-12 sm:py-16" data-testid="resource-article">
        <Breadcrumbs items={p.breadcrumbs} />
        {p.section ? (
          <Link to={`/resources/${p.section}`} className="text-xs font-semibold uppercase tracking-wide text-editorial hover:underline" data-testid="resource-section-link">
            {SECTION_LABELS[p.section] || p.section}
          </Link>
        ) : null}
        <h1 className="mt-2 font-heading text-4xl font-semibold leading-tight text-navy sm:text-5xl">
          {p.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-400">
          <span data-testid="resource-byline">By {p.author}{p.author_title ? `, ${p.author_title}` : ""}</span>
          {p.published_at ? <span>· Published {fmtDate(p.published_at)}</span> : null}
          {p.updated_at ? <span>· Updated {fmtDate(p.updated_at)}</span> : null}
        </div>

        {p.section === "ai-costs" && (p.pricing_checked_at || p.updated_at) ? (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-sm text-emerald-800" data-testid="resource-last-verified">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
            </svg>
            Prices checked {fmtDate(p.pricing_checked_at || p.updated_at)} against official vendor pages
          </div>
        ) : null}

        {p.featured_image ? (
          <figure className="mt-8" data-testid="resource-featured-image">
            <img src={p.featured_image.url} alt={p.featured_image.alt} className="w-full rounded-xl border border-slate-200 object-cover" />
            {p.featured_image.caption ? (
              <figcaption className="mt-2 text-center text-sm text-slate-400">{p.featured_image.caption}</figcaption>
            ) : null}
          </figure>
        ) : null}

        {p.summary_answer ? (
          <div className="mt-8 rounded-xl border-l-4 border-editorial bg-editorial/5 p-5" data-testid="resource-summary">
            <p className="text-base leading-relaxed text-slate-700">{p.summary_answer}</p>
          </div>
        ) : null}

        {p.definition_block ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-sm leading-relaxed text-slate-600">{p.definition_block}</p>
          </div>
        ) : null}

        {/* Above-the-fold primary CTA (CRO) */}
        <InlineCtaBanner primary={p.primary_cta} secondary={p.secondary_cta} testid="resource-cta-top" />

        <LandingDetails landing={p.landing} />

        <KeyFeatures features={p.landing?.key_features} />

        {p.content_type === "calculator_page" ? (
          <CalculatorWidget primaryCta={p.primary_cta} type={p.landing?.calculator_type} />
        ) : null}

        {/* Article body with periodic in-article CTAs (CRO) */}
        <ResourceArticleBody
          html={p.body_html}
          primaryCta={p.primary_cta}
          secondaryCta={p.secondary_cta}
        />

        <ComparisonTable data={p.landing?.comparison_table} caption={`${p.title} comparison`} />

        <ChecklistBlock items={p.landing?.checklist_items} />

        {p.proof_blocks?.map((b) => <ProofBlock key={b.id} block={b} />)}

        <FaqSection items={p.faq_items} />

        <CtaBlock primary={p.primary_cta} secondary={p.secondary_cta} />

        {p.author_bio ? (
          <div className="mt-12 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="author-box">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">About the author</p>
            <p className="mt-2 text-sm font-semibold text-navy">{p.author}{p.author_title ? ` · ${p.author_title}` : ""}</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.author_bio}</p>
            {p.author_url ? (
              <a href={p.author_url} target="_blank" rel="noopener noreferrer author" className="mt-2 inline-block text-sm font-medium text-editorial underline" data-testid="author-linkedin-link">
                Paige Gilmore on LinkedIn
              </a>
            ) : null}
          </div>
        ) : null}

        <RelatedPages pages={p.related_pages} />
      </article>
      <Footer />
    </div>
  );
}
